import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';

interface CompressionHistoryEntry {
  id: string;
  created_at: string;
  total_images: number;
  original_size_bytes: number;
  compressed_size_bytes: number;
  compression_ratio: number;
  quality_setting: number;
  preset_used: string;
  auto_resize_enabled: boolean;
  max_width?: number;
  max_height?: number;
}

interface SaveCompressionData {
  totalImages: number;
  originalSizeBytes: number;
  compressedSizeBytes: number;
  qualitySetting: number;
  presetUsed: string;
  autoResizeEnabled: boolean;
  maxWidth?: number;
  maxHeight?: number;
}

export const useCompressionHistory = () => {
  const [history, setHistory] = useState<CompressionHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchHistory = async () => {
    if (!user) {
      setHistory([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('compression_history')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error fetching compression history:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el historial de compresión",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveCompressionHistory = async (data: SaveCompressionData) => {
    if (!user) {
      console.log('No user found, cannot save compression history');
      return;
    }

    const compressionRatio = ((data.compressedSizeBytes / data.originalSizeBytes) * 100);
    
    console.log('Saving compression history:', {
      user_id: user.id,
      total_images: data.totalImages,
      original_size: data.originalSizeBytes,
      compressed_size: data.compressedSizeBytes,
      compression_ratio: compressionRatio,
      preset: data.presetUsed
    });
    
    try {
      const { error } = await supabase
        .from('compression_history')
        .insert({
          user_id: user.id,
          total_images: data.totalImages,
          original_size_bytes: data.originalSizeBytes,
          compressed_size_bytes: data.compressedSizeBytes,
          compression_ratio: compressionRatio,
          quality_setting: data.qualitySetting,
          preset_used: data.presetUsed,
          auto_resize_enabled: data.autoResizeEnabled,
          max_width: data.maxWidth,
          max_height: data.maxHeight,
        });

      if (error) {
        console.error('Supabase error saving compression history:', error);
        throw error;
      }
      
      console.log('Successfully saved compression history');
      
      // Refresh history after saving
      await fetchHistory();
      
      toast({
        title: "Historial actualizado",
        description: "Se guardó el registro de compresión",
      });
    } catch (error) {
      console.error('Error saving compression history:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar el historial de compresión",
        variant: "destructive",
      });
    }
  };

  const deleteHistoryEntry = async (id: string) => {
    try {
      const { error } = await supabase
        .from('compression_history')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      await fetchHistory();
      toast({
        title: "Eliminado",
        description: "Entrada del historial eliminada",
      });
    } catch (error) {
      console.error('Error deleting history entry:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la entrada del historial",
        variant: "destructive",
      });
    }
  };

  const getTotalStats = () => {
    const totalImages = history.reduce((sum, entry) => sum + entry.total_images, 0);
    const totalOriginalSize = history.reduce((sum, entry) => sum + entry.original_size_bytes, 0);
    const totalCompressedSize = history.reduce((sum, entry) => sum + entry.compressed_size_bytes, 0);
    const totalSaved = totalOriginalSize - totalCompressedSize;
    const averageCompression = totalOriginalSize > 0 ? (totalCompressedSize / totalOriginalSize) * 100 : 0;

    return {
      totalImages,
      totalOriginalSize,
      totalCompressedSize,
      totalSaved,
      averageCompression,
      totalCompressions: history.length,
    };
  };

  useEffect(() => {
    fetchHistory();
  }, [user]);

  return {
    history,
    loading,
    saveCompressionHistory,
    deleteHistoryEntry,
    refreshHistory: fetchHistory,
    getTotalStats,
  };
};